import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { colors, borderRadius, shadows } from '../styles/designTokens';
import { FiCheck, FiX, FiChevronDown } from 'react-icons/fi';
import { formatNumber } from '../utils/numberFormatter';
import { formatDateTimeKST } from '../utils/dateUtils';
import { CustomerService } from '../services/CustomerService';
import KeywordDataSidePanel from './KeywordDataSidePanel';
import { KeywordSetting } from '../models/CustomerModels';

// 모바일 키워드 설정 컨테이너
const MobileKeywordContainer = styled.div`
  background: ${colors.semantic.background.primary};
  border-radius: ${borderRadius.xl};
  overflow: hidden;
  box-shadow: ${shadows.base};
`;

// 모바일 키워드 설정 헤더
const MobileKeywordHeader = styled.div`
  background: linear-gradient(
    135deg,
    ${colors.semantic.background.secondary} 0%,
    ${colors.semantic.background.tertiary} 100%
  );
  padding: 16px;
  border-bottom: 1px solid ${colors.semantic.border.primary};
`;

// 모바일 키워드 설정 제목
const MobileKeywordTitle = styled.h3`
  font-size: 16px;
  font-weight: 700;
  color: ${colors.secondary[800]};
  margin: 0;
  line-height: 1.4;
`;

// 모바일 키워드 카드 - 아코디언 스타일
const MobileKeywordCard = styled.div<{ $isExpanded: boolean }>`
  border-bottom: 1px solid ${colors.neutral[200]};
  background: ${props =>
    props.$isExpanded ? colors.neutral[50] : colors.semantic.background.primary};
  transition: background-color 0.2s ease;

  &:last-child {
    border-bottom: none;
  }

  &:active {
    background: ${colors.neutral[100]};
  }
`;

// 모바일 키워드 카드 헤더 - 클릭 가능
const MobileKeywordCardHeader = styled.button`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 16px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: ${colors.neutral[50]};
  }

  &:active {
    background: ${colors.neutral[100]};
  }
`;

// 모바일 키워드 이름 영역
const MobileKeywordNameArea = styled.div`
  flex: 1;
  min-width: 0;
  margin-right: 12px;
`;

// 모바일 키워드 이름
const MobileKeywordName = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.secondary[900]};
  margin: 0 0 4px 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

// 모바일 키워드 상태 및 액션 영역
const MobileKeywordActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

// 모바일 키워드 상태 버튼
const MobileKeywordStatus = styled.div<{ isActive: boolean }>`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 600;
  background: ${props =>
    props.isActive
      ? `linear-gradient(135deg, ${colors.success[100]} 0%, ${colors.success[200]} 100%)`
      : `linear-gradient(135deg, ${colors.error[100]} 0%, ${colors.error[200]} 100%)`};
  color: ${props => (props.isActive ? colors.success[800] : colors.error[600])};
  box-shadow: ${shadows.sm};
  white-space: nowrap;
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

// 확장/축소 아이콘
const ExpandIcon = styled.div<{ $isExpanded: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  color: ${colors.semantic.text.tertiary};
  transition: transform 0.2s ease;
  transform: rotate(${props => props.$isExpanded ? '180deg' : '0deg'});
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

// 모바일 키워드 상세 정보 영역 - 아코디언 콘텐츠
const MobileKeywordDetails = styled.div<{ $isExpanded: boolean }>`
  max-height: ${props => props.$isExpanded ? '1000px' : '0'};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${props => props.$isExpanded ? '0 16px 16px 16px' : '0 16px'};
`;

// 모바일 키워드 정보 그리드
const MobileKeywordInfoGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  padding-top: 12px;
  font-size: 13px;
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 10px;
  }
`;

// 모바일 키워드 정보 아이템
const MobileKeywordInfoItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  
  .label {
    font-weight: 500;
    font-size: 11px;
    color: ${colors.semantic.text.tertiary};
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .value {
    color: ${colors.secondary[900]};
    font-size: 13px;
    font-weight: 600;
    line-height: 1.4;
    word-break: break-word;
  }
`;

// 수집 데이터 보기 버튼
const ViewDataButton = styled.button<{ disabled: boolean }>`
  width: 100%;
  margin-top: 12px;
  padding: 12px 16px;
  border: 1px solid
    ${props => (props.disabled ? colors.neutral[200] : colors.primary[500])};
  border-radius: ${borderRadius.lg};
  background: ${props =>
    props.disabled ? colors.neutral[100] : colors.semantic.background.primary};
  color: ${props =>
    props.disabled ? colors.semantic.text.disabled : colors.primary[500]};
  font-size: 13px;
  font-weight: 600;
  cursor: ${props => (props.disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;

  &:not(:disabled):hover {
    background: ${colors.primary[50]};
    border-color: ${colors.primary[600]};
    color: ${colors.primary[600]};
    transform: translateY(-1px);
    box-shadow: ${shadows.primary};
  }
  
  &:not(:disabled):active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.6;
  }
`;


// 모바일 로딩 스피너
const MobileLoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 32px;
  
  .spinner {
    width: 24px;
    height: 24px;
    border: 2px solid ${colors.semantic.border.primary};
    border-top: 2px solid ${colors.primary[500]};
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// 모바일 에러 메시지
const MobileErrorMessage = styled.div`
  background: ${colors.error[50]};
  border: 1px solid ${colors.error[200]};
  color: ${colors.error[600]};
  padding: 12px 16px;
  border-radius: ${borderRadius.md};
  text-align: center;
  font-size: 12px;
  margin: 16px;
  line-height: 1.3;
`;

// 모바일 빈 상태 메시지
const MobileEmptyState = styled.div`
  text-align: center;
  padding: 32px 16px;
  color: ${colors.semantic.text.tertiary};

  h3 {
    font-size: 14px;
    font-weight: 600;
    color: ${colors.semantic.text.secondary};
    margin: 0 0 6px 0;
  }
  
  p {
    font-size: 12px;
    margin: 0;
    line-height: 1.3;
  }
`;

// 키워드 데이터 타입은 KeywordSetting을 사용

// 모바일 키워드 설정 테이블 컴포넌트
interface KeywordSettingTableMobileProps {
  userIdx: number;
}

const KeywordSettingTableMobile: React.FC<KeywordSettingTableMobileProps> = ({ userIdx }) => {
  const [keywords, setKeywords] = useState<KeywordSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());
  const [selectedKeyword, setSelectedKeyword] = useState<KeywordSetting | null>(null);
  const [isDataPanelOpen, setIsDataPanelOpen] = useState(false);

  useEffect(() => {
    const fetchKeywords = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('📱 모바일 키워드 설정 API 호출 시작:', userIdx);
        const response = await CustomerService.getKeywordSettings({
          userIdx,
          page: 1,
          limit: 15
        });

        console.log('📱 모바일 키워드 설정 API 응답 성공:', response);
        setKeywords(response.items || []);
      } catch (err: any) {
        console.error('📱 모바일 키워드 설정 조회 실패:', err);
        setError(err.message || '키워드 설정을 불러오는데 실패했습니다.');
      } finally {
        setLoading(false);
      }
    };

    if (userIdx) {
      fetchKeywords();
    }
  }, [userIdx]);

  // 활성화 상태 확인 함수
  const isActive = (status: string) => status === 'ON';

  // 카드 확장/축소 토글
  const toggleExpand = (index: number) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedItems(newExpanded);
  };

  // 키워드 수집 데이터 보기
  const handleViewData = (keyword: KeywordSetting, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!keyword.keyword_idx) {
      return;
    }
    setSelectedKeyword(keyword);
    setIsDataPanelOpen(true);
  };

  // 키워드 수집 데이터 패널 닫기
  const handleCloseDataPanel = () => {
    setIsDataPanelOpen(false);
    setSelectedKeyword(null);
  };

  if (loading) {
    return (
      <MobileKeywordContainer>
        <MobileKeywordHeader>
          <MobileKeywordTitle>키워드 설정</MobileKeywordTitle>
        </MobileKeywordHeader>
        <MobileLoadingContainer>
          <div className="spinner" />
        </MobileLoadingContainer>
      </MobileKeywordContainer>
    );
  }

  if (error) {
    return (
      <MobileKeywordContainer>
        <MobileKeywordHeader>
          <MobileKeywordTitle>키워드 설정</MobileKeywordTitle>
        </MobileKeywordHeader>
        <MobileErrorMessage>{error}</MobileErrorMessage>
      </MobileKeywordContainer>
    );
  }

  return (
    <MobileKeywordContainer>
      <MobileKeywordHeader>
        <MobileKeywordTitle>
          키워드 설정 {keywords && keywords.length > 0 && `(${keywords.length}개)`}
        </MobileKeywordTitle>
      </MobileKeywordHeader>

      {!keywords || keywords.length === 0 ? (
        <MobileEmptyState>
          <h3>키워드가 없습니다</h3>
          <p>아직 설정된 키워드가 없습니다.</p>
        </MobileEmptyState>
      ) : (
        keywords.map((keyword, index) => {
          const isExpanded = expandedItems.has(index);
          return (
            <MobileKeywordCard key={`${keyword.생성일자}-${index}`} $isExpanded={isExpanded}>
              <MobileKeywordCardHeader onClick={() => toggleExpand(index)}>
                <MobileKeywordNameArea>
                  <MobileKeywordName>{keyword.키워드}</MobileKeywordName>
                </MobileKeywordNameArea>
                <MobileKeywordActions>
                  <MobileKeywordStatus isActive={isActive(keyword.활성화)}>
                    {isActive(keyword.활성화) ? <FiCheck /> : <FiX />}
                    {isActive(keyword.활성화) ? '활성' : '비활성'}
                  </MobileKeywordStatus>
                  <ExpandIcon $isExpanded={isExpanded}>
                    <FiChevronDown />
                  </ExpandIcon>
                </MobileKeywordActions>
              </MobileKeywordCardHeader>

              <MobileKeywordDetails $isExpanded={isExpanded}>
                <MobileKeywordInfoGrid>
                  <MobileKeywordInfoItem>
                    <div className="label">브랜드명</div>
                    <div className="value">{keyword.브랜드명}</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">타겟사이트</div>
                    <div className="value">{keyword.타겟사이트}</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">분석유형</div>
                    <div className="value">{keyword.분석유형}</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">수집주기</div>
                    <div className="value">{keyword.수집주기}</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">수집량</div>
                    <div className="value">{formatNumber(keyword.수집량)}건</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">분석량</div>
                    <div className="value">{formatNumber(keyword.분석량)}건</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">생성일</div>
                    <div className="value">{formatDateTimeKST(keyword.생성일자)}</div>
                  </MobileKeywordInfoItem>
                  <MobileKeywordInfoItem>
                    <div className="label">상태</div>
                    <div className="value">{keyword.활성화}</div>
                  </MobileKeywordInfoItem>
                </MobileKeywordInfoGrid>

                <ViewDataButton
                  disabled={!keyword.keyword_idx}
                  onClick={(e) => handleViewData(keyword, e)}
                >
                  수집 데이터 보기
                </ViewDataButton>
              </MobileKeywordDetails>
            </MobileKeywordCard>
          );
        })
      )}

      {/* 키워드 수집 데이터 사이드 패널 */}
      {selectedKeyword && isDataPanelOpen && selectedKeyword.keyword_idx && (
        <KeywordDataSidePanel
          userIdx={userIdx}
          keywordIdx={selectedKeyword.keyword_idx}
          keywordText={selectedKeyword.키워드}
          brandName={selectedKeyword.브랜드명}
          targetSite={selectedKeyword.타겟사이트}
          isOpen={isDataPanelOpen}
          onClose={handleCloseDataPanel}
        />
      )}
    </MobileKeywordContainer>
  );
};

export default KeywordSettingTableMobile;
